import React from 'react'
import UserContext from "../dataContext";
import * as XLSX from 'xlsx';

// Internal components
import Overlay from "../Components/Subcomponents/Overlay/Overlay";


// Icons
import { BsPlus, BsDownload } from "react-icons/bs";
import Spinner from '../Components/Spinner';
import HoursMatrixExport from '../Components/HoursMatrixExport';

export default function Clients(props) {
    const userContext = React.useContext(UserContext)

    const [item, setItem] = React.useState({})
        , [, r] = React.useState({})
    const [showExportDialog, setShowExportDialog] = React.useState(false);
    const [exportStartDate, setExportStartDate] = React.useState('');
    const [exportEndDate, setExportEndDate] = React.useState('');
    const [exportLoading, setExportLoading] = React.useState(false);
    const [separateValidated, setSeparateValidated] = React.useState(false);

    const forceUpdate = React.useCallback(() => {
        r({})
    }, [r]);

    const stripeNoticeHandled = React.useRef(false);

    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const stripeConnectStatus = params.get('stripe_connect');
        if (!stripeConnectStatus) return;

        sessionStorage.setItem('stripe_connect_notice', JSON.stringify({
            status: stripeConnectStatus,
            message: params.get('message')
        }));

        params.delete('stripe_connect');
        params.delete('message');
        const query = params.toString();
        window.history.replaceState({}, document.title, `${window.location.pathname}${query ? `?${query}` : ''}`);
    }, []);

    React.useEffect(() => {
        if (userContext.loading || stripeNoticeHandled.current) return;

        const rawNotice = sessionStorage.getItem('stripe_connect_notice');
        if (!rawNotice) return;

        stripeNoticeHandled.current = true;
        sessionStorage.removeItem('stripe_connect_notice');

        let notice;
        try {
            notice = JSON.parse(rawNotice);
        } catch (err) {
            console.error('Invalid Stripe Connect notice payload', err);
            return;
        }

        if (notice.status === 'connected') {
            userContext.setError({
                level: 'success',
                message: 'Compte Stripe connecté avec succès. Ouvrez le profil client et cliquez sur « Rafraîchir le statut » si besoin.',
                btn1Text: 'OK',
                btn2hidden: true
            });
        } else if (notice.status === 'return') {
            userContext.setError({
                level: 'info',
                message: 'Retour depuis Stripe. Rafraîchissez le statut du client si nécessaire.',
                btn1Text: 'OK',
                btn2hidden: true
            });
        } else if (notice.status === 'refresh') {
            userContext.setError({
                level: 'warning',
                message: 'La configuration Stripe a expiré ou a été interrompue. Relancez la configuration depuis le profil client.',
                btn1Text: 'OK',
                btn2hidden: true
            });
        } else if (notice.status === 'error') {
            userContext.setError({
                level: 'error',
                message: decodeURIComponent(notice.message || 'Une erreur est survenue lors de la connexion Stripe.'),
                btn1Text: 'OK',
                btn2hidden: true
            });
        }
    }, [userContext.loading, userContext.setError]);

    const clients = userContext.data?.people?.Client;

    const exportInterventionsToExcel = async () => {
        try {
            setExportLoading(true);

            if (!exportStartDate || !exportEndDate) {
                userContext.setError({
                    level: 'warning',
                    message: 'Veuillez sélectionner une période (date de début et date de fin)',
                    btn1Text: 'OK',
                    btn2hidden: true
                });
                setExportLoading(false);
                return;
            }

            const startDate = new Date(exportStartDate);
            const endDate = new Date(exportEndDate);
            endDate.setHours(23, 59, 59, 999);

            userContext.setError({
                level: 'info',
                message: `Calcul des heures d'intervention du ${startDate.toLocaleDateString('fr-FR')} au ${endDate.toLocaleDateString('fr-FR')}...`,
                btn1Text: 'Patienter...',
                btn1hidden: true,
                btn2hidden: true
            });

            const clientRows = clients || [];

            const getMonthsBetweenDates = (start, end) => {
                const months = [];
                const current = new Date(start.getFullYear(), start.getMonth(), 1);
                const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

                while (current <= endMonth) {
                    months.push({
                        year: current.getFullYear(),
                        month: current.getMonth() + 1,
                        label: current.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
                    });
                    current.setMonth(current.getMonth() + 1);
                }

                return months;
            };

            const durationToHours = (durationString) => {
                if (!durationString) return 0;
                const parts = durationString.split(':');
                const hours = parseInt(parts[0]) || 0;
                const minutes = parseInt(parts[1]) || 0;
                return hours + (minutes / 60);
            };

            const months = getMonthsBetweenDates(startDate, endDate);

            const isInterventionValidated = (intervention) => {
                if (!intervention.people || intervention.people.length === 0) return false;
                return intervention.people.every(person => person.report && person.finishDate);
            };

            const clientData = clientRows
                .filter(client => !client.archive)
                .map(client => {
                    const clientName = `${client.lastName || ''} ${client.name || ''}`.trim() || client.email;
                    const monthlyHours = {};
                    const monthlyValidatedHours = {};
                    const monthlyNonValidatedHours = {};

                    months.forEach(month => {
                        const key = `${month.year}-${month.month}`;
                        monthlyHours[key] = 0;
                        monthlyValidatedHours[key] = 0;
                        monthlyNonValidatedHours[key] = 0;
                    });

                    if (client.properties && Array.isArray(client.properties)) {
                        client.properties.forEach(property => {
                            if (property.interventions && Array.isArray(property.interventions)) {
                                property.interventions.forEach(intervention => {
                                    if (intervention.cancelled) return;

                                    const interventionDate = new Date(intervention.date?.dateStart);
                                    if (interventionDate >= startDate && interventionDate <= endDate) {
                                        const year = interventionDate.getFullYear();
                                        const month = interventionDate.getMonth() + 1;
                                        const key = `${year}-${month}`;

                                        if (monthlyHours.hasOwnProperty(key)) {
                                            const hours = durationToHours(intervention.date?.duration);
                                            monthlyHours[key] += hours;

                                            if (separateValidated) {
                                                if (isInterventionValidated(intervention)) {
                                                    monthlyValidatedHours[key] += hours;
                                                } else {
                                                    monthlyNonValidatedHours[key] += hours;
                                                }
                                            }
                                        }
                                    }
                                });
                            }
                        });
                    }

                    return {
                        clientName,
                        monthlyHours,
                        monthlyValidatedHours,
                        monthlyNonValidatedHours,
                        totalHours: Object.values(monthlyHours).reduce((sum, hours) => sum + hours, 0),
                        totalValidatedHours: Object.values(monthlyValidatedHours).reduce((sum, hours) => sum + hours, 0),
                        totalNonValidatedHours: Object.values(monthlyNonValidatedHours).reduce((sum, hours) => sum + hours, 0)
                    };
                })
                .filter(data => data.totalHours > 0);

            if (clientData.length === 0) {
                userContext.setError({
                    level: 'warning',
                    message: 'Aucune intervention trouvée dans la période sélectionnée',
                    btn1Text: 'OK',
                    btn2hidden: true
                });
                setExportLoading(false);
                return;
            }

            // Create Excel data as 2D array
            let headers;
            if (separateValidated) {
                // Create two columns per month: Validated and Non-validated
                headers = [
                    'Client',
                    ...months.flatMap(m => [`${m.label} (Validées)`, `${m.label} (Non validées)`]),
                    'Total Validées',
                    'Total Non validées',
                    'Total Général'
                ];
            } else {
                headers = ['Client', ...months.map(m => m.label), 'Total'];
            }

            const excelData = [headers];

            clientData.forEach(data => {
                let row;
                if (separateValidated) {
                    row = [
                        data.clientName,
                        ...months.flatMap(m => {
                            const key = `${m.year}-${m.month}`;
                            return [
                                data.monthlyValidatedHours[key] || 0,
                                data.monthlyNonValidatedHours[key] || 0
                            ];
                        }),
                        data.totalValidatedHours,
                        data.totalNonValidatedHours,
                        data.totalHours
                    ];
                } else {
                    row = [
                        data.clientName,
                        ...months.map(m => {
                            const key = `${m.year}-${m.month}`;
                            return data.monthlyHours[key] || 0;
                        }),
                        data.totalHours
                    ];
                }
                excelData.push(row);
            });

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(excelData);

            // Auto-size columns
            const maxWidths = excelData[0].map((_, colIndex) => {
                return Math.max(
                    ...excelData.map(row => {
                        const cellValue = row[colIndex];
                        return cellValue ? cellValue.toString().length : 10;
                    })
                );
            });

            ws['!cols'] = maxWidths.map(width => ({ wch: Math.min(width + 2, 50) }));

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Interventions');

            // Generate filename
            const dateRangeStr = `${exportStartDate.replace(/-/g, '')}_${exportEndDate.replace(/-/g, '')}`;
            const filename = `interventions_clients_${dateRangeStr}.xlsx`;

            // Write file
            XLSX.writeFile(wb, filename);

            setExportLoading(false);

            let successMessage;
            if (separateValidated) {
                const totalValidated = clientData.reduce((sum, c) => sum + c.totalValidatedHours, 0);
                const totalNonValidated = clientData.reduce((sum, c) => sum + c.totalNonValidatedHours, 0);
                const totalGeneral = clientData.reduce((sum, c) => sum + c.totalHours, 0);
                successMessage = `Export terminé avec succès pour ${clientData.length} clients.\n` +
                    `Validées: ${totalValidated.toFixed(2)}h | Non validées: ${totalNonValidated.toFixed(2)}h | Total: ${totalGeneral.toFixed(2)}h`;
            } else {
                const totalHours = clientData.reduce((sum, c) => sum + c.totalHours, 0);
                successMessage = `Export terminé avec succès pour ${clientData.length} clients. Total: ${totalHours.toFixed(2)} heures.`;
            }

            userContext.setError({
                level: 'success',
                message: successMessage,
                btn1Text: 'OK',
                btn2hidden: true
            });

        } catch (error) {
            console.error("Error exporting interventions to Excel:", error);
            setExportLoading(false);
            userContext.setError({
                level: 'error',
                message: `Erreur lors de l'export: ${error.message || 'Erreur inconnue'}`,
                btn1Text: 'OK',
                btn2hidden: true
            });
        }
    };

    if (userContext.loading || !clients) {
        return (
            <div className={"col border cardsContainer people-directory mx-1 p-2 mt-3 mt-md-0"}>
                <div className={"row justify-content-between align-items-center mb-3"}>
                    <div className={"col-auto"}>
                        <h1>Clients</h1>
                    </div>
                    <div className={"col-auto d-flex gap-2"}>
                        <button
                            type={"button"}
                            className={'btn btn-outline-success'}
                            disabled
                        >
                            <BsDownload className="me-2" />
                            Extraction des interventions
                        </button>
                        <button type={"button"} className={'btn btn-outline-dark'} onClick={() => {
                            setItem({ choices: "Clients" })
                        }}>
                            <h3><BsPlus /></h3>
                        </button>
                    </div>
                </div>
                <table className="table table-hover people-table">
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Prénom</th>
                            <th>Téléphone</th>
                            <th>Email</th>
                            <th>Professionel</th>
                            <th>Client abonné</th>
                        </tr>
                    </thead>
                    <tbody className='my-5'>
                        <tr>
                            <td colSpan={6}>
                                <Spinner strokeWidth={9} width={40} color={"#003952"} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        )
    }

    return (
        <div className={"col border cardsContainer people-directory mx-1 p-2 mt-3 mt-md-0"}>
            <div className={"row justify-content-between align-items-center mb-3"}>
                <div className={"col-auto"}>
                    <h1>Clients</h1>
                </div>
                <div className={"col-auto d-flex gap-2"}>
                    <button
                        type={"button"}
                        className={'btn btn-outline-success'}
                        onClick={() => setShowExportDialog(!showExportDialog)}
                    >
                        <BsDownload className="me-2" />
                        Extraction des interventions {showExportDialog ? '▲' : '▼'}
                    </button>
                    <button type={"button"} className={'btn btn-outline-dark'} onClick={() => {
                        let object = {}
                        object.choices = "Client"
                        setItem(object)
                    }}>
                        <h3><BsPlus /></h3>
                    </button>
                </div>
            </div>

            {/* Export dialog section */}
            {showExportDialog && (
                <div className="filters-panel mt-3 p-3 border rounded bg-light mb-3">
                    <div className="row align-items-end">
                        <div className="col-12 mb-3">
                            <h5>Extraction des heures d'intervention par client</h5>
                            <p className="small text-muted">
                                Sélectionnez une période pour calculer le cumul d'heures d'intervention par mois pour chaque client.
                                Le fichier Excel généré préservera tous les caractères accentués (é, è, à, etc.).
                            </p>
                        </div>
                        <div className="col-12 mb-3">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="separateValidatedCheckbox"
                                    checked={separateValidated}
                                    onChange={(e) => setSeparateValidated(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="separateValidatedCheckbox">
                                    Séparer interventions validées et non validées
                                    <small className="d-block text-muted">
                                        Une intervention est validée si tous les intervenants ont un rapport et une date de fin.
                                    </small>
                                </label>
                            </div>
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Date de début <span className="text-danger">*</span></label>
                            <input
                                type="date"
                                className="form-control"
                                value={exportStartDate}
                                onChange={(e) => setExportStartDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Date de fin <span className="text-danger">*</span></label>
                            <input
                                type="date"
                                className="form-control"
                                value={exportEndDate}
                                onChange={(e) => setExportEndDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <button
                                className="btn btn-success w-100"
                                onClick={exportInterventionsToExcel}
                                disabled={exportLoading || !exportStartDate || !exportEndDate}
                            >
                                {exportLoading ? (
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                ) : (
                                    <BsDownload className="me-2" />
                                )}
                                Exporter en Excel
                            </button>
                        </div>
                    </div>

                    <HoursMatrixExport
                        clients={clients || []}
                        concierges={userContext.data?.people?.Concierge || []}
                        setError={userContext.setError}
                    />
                </div>
            )}

            <table className="table table-hover people-table">
                <thead>
                    <tr>
                        <th>Nom</th>
                        <th>Prénom</th>
                        <th>Téléphone</th>
                        <th>Email</th>
                        <th>Professionel</th>
                        <th>Client abonné</th>
                    </tr>
                </thead>
                <tbody>
                    {(clients || []).sort((a, b) => a.lastName > b.lastName ? 1 : -1).map((client) => {
                        if (client.archive) return null
                        return (
                            <tr onClick={() => {
                                const object = client
                                object.choices = "ClientChoices"
                                setItem(object)
                            }}>
                                <td>{client.lastName}</td>
                                <td>{client.name}</td>
                                <td>{client.phone}</td>
                                <td>{client.email}</td>
                                <td>{client.professional ? "Oui " + client.companyID : "Non"}</td>
                                <td>{client.subscription ? (client.subscription.aborted.isAborted ? "Contrat terminé" : "Oui ") : "Non"}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
            <Overlay item={item}
                close={() => {
                    setItem(false)
                }}
                edit={(value) => {
                    setItem(value)
                    forceUpdate()
                }} />
        </div>
    )
}